import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, FloppyDisk, DotsSixVertical } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = [
  { value: 'global', label: 'Global (todos)' },
  { value: 'gravida', label: 'Gravidez' },
  { value: 'pos_parto', label: 'Pós-parto' },
  { value: 'introducao_alimentar', label: 'Introdução alimentar' },
  { value: 'emagrecimento', label: 'Emagrecimento' },
  { value: 'pre_concecao', label: 'Pré-conceção' },
  { value: 'geral', label: 'Genérico' },
];
const Q_TYPES = [
  { value: 'text', label: 'Texto livre' },
  { value: 'select', label: 'Seleção simples' },
  { value: 'multiselect', label: 'Seleção múltipla' },
  { value: 'scale', label: 'Escala numérica' },
  { value: 'date', label: 'Data' },
  { value: 'yesno', label: 'Sim/Não' },
];

export default function AnamneseEditorPage() {
  useRequireAdmin();
  const [templates, setTemplates] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [showAddQ, setShowAddQ] = useState(null);
  const [qForm, setQForm] = useState({ text: '', type: 'text', required: false, options: '' });
  const [editingQ, setEditingQ] = useState(null);
  // Drag state
  const [dragSection, setDragSection] = useState(null);
  const [overSection, setOverSection] = useState(null);
  const [dragQ, setDragQ] = useState(null); // { si, qi }
  const [overQ, setOverQ] = useState(null); // { si, qi }

  const fetchTemplates = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/anamnese-templates`); setTemplates(data); } catch {}
  }, []);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const loadTemplate = async (program) => {
    setSelectedProgram(program);
    try {
      const { data } = await axios.get(`${API}/api/anamnese-templates/${program}`);
      setSections(data?.sections || []);
    } catch { setSections([]); }
  };

  const save = async () => {
    if (!selectedProgram) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/anamnese-templates/${selectedProgram}`, { sections });
      toast.success('Template guardado');
      fetchTemplates();
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const addSection = () => {
    if (!newSectionName.trim()) return;
    setSections([...sections, { name: newSectionName, questions: [] }]);
    setNewSectionName(''); setShowAddSection(false);
  };
  const removeSection = (si) => setSections(sections.filter((_, i) => i !== si));
  const renameSec = (si, name) => { const s = [...sections]; s[si] = { ...s[si], name }; setSections(s); };

  // Section drag
  const handleSecDragStart = (e, si) => { setDragSection(si); e.dataTransfer.effectAllowed = 'move'; };
  const handleSecDragOver = (e, si) => { e.preventDefault(); setOverSection(si); };
  const handleSecDrop = (e, si) => {
    e.preventDefault(); setOverSection(null);
    if (dragSection === null || dragSection === si) { setDragSection(null); return; }
    const arr = [...sections];
    const [moved] = arr.splice(dragSection, 1);
    arr.splice(si > dragSection ? si - 1 : si, 0, moved);
    setSections(arr);
    setDragSection(null);
  };
  const handleSecDragEnd = () => { setDragSection(null); setOverSection(null); };

  // Question drag (within and between sections)
  const handleQDragStart = (e, si, qi) => {
    e.stopPropagation();
    setDragQ({ si, qi });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ si, qi }));
  };
  const handleQDragOver = (e, si, qi) => { e.preventDefault(); e.stopPropagation(); setOverQ({ si, qi }); };
  const handleQDrop = (e, targetSi, targetQi) => {
    e.preventDefault(); e.stopPropagation(); setOverQ(null);
    if (!dragQ) return;
    const { si: srcSi, qi: srcQi } = dragQ;
    if (srcSi === targetSi && srcQi === targetQi) { setDragQ(null); return; }
    const s = sections.map(sec => ({ ...sec, questions: [...sec.questions] }));
    const [moved] = s[srcSi].questions.splice(srcQi, 1);
    const insertIdx = srcSi === targetSi && targetQi > srcQi ? targetQi - 1 : targetQi;
    s[targetSi].questions.splice(insertIdx, 0, moved);
    setSections(s);
    setDragQ(null);
  };
  const handleQDragEnd = () => { setDragQ(null); setOverQ(null); };
  // Drop on section header = append to that section
  const handleSectionHeaderDrop = (e, targetSi) => {
    e.preventDefault(); e.stopPropagation(); setOverSection(null); setOverQ(null);
    if (!dragQ) { handleSecDrop(e, targetSi); return; }
    const { si: srcSi, qi: srcQi } = dragQ;
    if (srcSi === targetSi) { setDragQ(null); return; }
    const s = sections.map(sec => ({ ...sec, questions: [...sec.questions] }));
    const [moved] = s[srcSi].questions.splice(srcQi, 1);
    s[targetSi].questions.push(moved);
    setSections(s);
    setDragQ(null);
  };

  const addQuestion = (si) => {
    const s = [...sections];
    const opts = qForm.options ? qForm.options.split(',').map(o => o.trim()).filter(Boolean) : [];
    s[si].questions.push({ text: qForm.text, type: qForm.type, required: qForm.required, options: opts });
    setSections(s);
    setQForm({ text: '', type: 'text', required: false, options: '' });
    setShowAddQ(null);
  };
  const removeQuestion = (si, qi) => { const s = [...sections]; s[si].questions.splice(qi, 1); setSections(s); };
  const saveEditQ = (si, qi) => {
    const s = [...sections];
    const opts = qForm.options ? qForm.options.split(',').map(o => o.trim()).filter(Boolean) : [];
    s[si].questions[qi] = { ...s[si].questions[qi], text: qForm.text, type: qForm.type, required: qForm.required, options: opts };
    setSections(s);
    setEditingQ(null);
  };
  const updateQText = (si, qi, text) => { const s = [...sections]; s[si].questions[qi] = { ...s[si].questions[qi], text }; setSections(s); };

  const progLabel = (p) => PROGRAMS.find(x => x.value === p)?.label || p;

  return (
    <div className="fade-in" data-testid="anamnese-editor-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Templates de Anamnese</h2>
          <p className="text-xs text-brand-muted mt-0.5">Perguntas da ficha de avaliação inicial por programa</p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 flex-wrap">
        {PROGRAMS.map(p => {
          const hasTemplate = templates.some(t => t.program === p.value);
          return (
            <button key={p.value} onClick={() => loadTemplate(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${selectedProgram === p.value ? 'bg-brand-green text-white' : hasTemplate ? 'bg-brand-green/10 text-brand-green border border-brand-green/20' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
              data-testid={`prog-${p.value}`}>
              {p.label} {hasTemplate && selectedProgram !== p.value && <span className="ml-0.5 text-[8px]">*</span>}
            </button>
          );
        })}
      </div>

      {!selectedProgram ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <p className="text-brand-muted text-sm">Seleciona um programa para editar o template de anamnese.</p>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium text-sm text-brand-text">{progLabel(selectedProgram)}</h3>
            <div className="flex gap-2">
              <button onClick={() => setShowAddSection(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:bg-brand-bg" data-testid="add-section-btn">
                <Plus size={12} className="inline mr-1" /> Secção
              </button>
              <button onClick={save} disabled={saving} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50" data-testid="save-template-btn">
                <FloppyDisk size={12} /> {saving ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>

          {sections.length === 0 ? (
            <div className="text-center py-8 bg-white border border-brand-border rounded-2xl">
              <p className="text-brand-muted text-sm">Sem secções. Adiciona a primeira secção ao template.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((sec, si) => (
                <div key={si}
                  className={`bg-white border rounded-2xl p-4 transition-all ${overSection === si && dragSection !== null && dragSection !== si ? 'border-brand-green border-2 bg-brand-green/5' : 'border-brand-border'} ${dragSection === si ? 'opacity-40' : ''}`}
                  draggable onDragStart={e => handleSecDragStart(e, si)} onDragEnd={handleSecDragEnd}
                  onDragOver={e => { if (dragSection !== null) handleSecDragOver(e, si); else if (dragQ) { e.preventDefault(); setOverSection(si); } }}
                  onDrop={e => handleSectionHeaderDrop(e, si)}
                  data-testid={`section-${si}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={14} /></div>
                      <InlineEdit value={sec.name} onSave={v => renameSec(si, v)} className="font-heading font-medium text-sm text-brand-green" testId={`section-name-${si}`} />
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => removeSection(si)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                    </div>
                  </div>

                  {sec.questions.length === 0 ? (
                    <p className="text-xs text-brand-placeholder py-2">Sem perguntas nesta secção.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {sec.questions.map((q, qi) => (
                        <div key={qi}
                          draggable onDragStart={e => handleQDragStart(e, si, qi)} onDragEnd={handleQDragEnd}
                          onDragOver={e => handleQDragOver(e, si, qi)}
                          onDrop={e => handleQDrop(e, si, qi)}
                          className={`flex items-center gap-2 bg-brand-bg/30 rounded-xl px-3 py-2 group transition-all ${dragQ?.si === si && dragQ?.qi === qi ? 'opacity-40' : ''} ${overQ?.si === si && overQ?.qi === qi && !(dragQ?.si === si && dragQ?.qi === qi) ? 'ring-2 ring-brand-green/40 bg-brand-green/5' : ''}`}
                          data-testid={`question-${si}-${qi}`}>
                          <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green flex-shrink-0" onMouseDown={e => e.stopPropagation()}><DotsSixVertical size={12} /></div>
                          <span className="text-[9px] text-brand-muted bg-brand-bg px-1.5 py-0.5 rounded flex-shrink-0">{Q_TYPES.find(t => t.value === q.type)?.label || q.type}</span>
                          <InlineEdit value={q.text} onSave={v => updateQText(si, qi, v)} className="text-xs text-brand-text flex-1" testId={`q-text-${si}-${qi}`} />
                          {q.required && <span className="text-[8px] text-red-500 flex-shrink-0">obrig.</span>}
                          <div className="hidden group-hover:flex gap-0.5 flex-shrink-0">
                            <button onClick={() => { setEditingQ({ si, qi }); setQForm({ text: q.text, type: q.type, required: q.required, options: (q.options || []).join(', ') }); }} className="text-brand-placeholder hover:text-brand-green p-0.5"><PencilSimple size={11} /></button>
                            <button onClick={() => removeQuestion(si, qi)} className="text-brand-placeholder hover:text-red-500 p-0.5"><Trash size={11} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button onClick={() => { setShowAddQ(si); setQForm({ text: '', type: 'text', required: false, options: '' }); }} className="text-xs text-brand-green flex items-center gap-1 mt-2" data-testid={`add-q-${si}`}>
                    <Plus size={11} /> Adicionar pergunta
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova secção</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome da secção</Label><Input value={newSectionName} onChange={e => setNewSectionName(e.target.value)} placeholder="Ex: Hábitos Alimentares" className="rounded-xl border-brand-border" data-testid="section-name-input" onKeyDown={e => { if (e.key === 'Enter') addSection(); }} /></div>
            <button onClick={addSection} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-add-section">Adicionar</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddQ !== null || editingQ !== null} onOpenChange={v => { if (!v) { setShowAddQ(null); setEditingQ(null); } }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editingQ ? 'Editar pergunta' : 'Nova pergunta'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Texto da pergunta</Label><Textarea value={qForm.text} onChange={e => setQForm({ ...qForm, text: e.target.value })} placeholder="Ex: Quantas refeições faz por dia?" className="rounded-xl border-brand-border" rows={2} data-testid="q-text-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Tipo de resposta</Label>
              <div className="flex flex-wrap gap-1.5">
                {Q_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => setQForm({ ...qForm, type: t.value })}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${qForm.type === t.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {(qForm.type === 'select' || qForm.type === 'multiselect') && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Opções (separadas por vírgula)</Label><Input value={qForm.options} onChange={e => setQForm({ ...qForm, options: e.target.value })} placeholder="Opção 1, Opção 2, Opção 3" className="rounded-xl border-brand-border" /></div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={qForm.required} onChange={e => setQForm({ ...qForm, required: e.target.checked })} className="rounded border-brand-border" />
              <span className="text-xs text-brand-muted">Pergunta obrigatória</span>
            </label>
            <button onClick={() => { if (editingQ) saveEditQ(editingQ.si, editingQ.qi); else if (showAddQ !== null) addQuestion(showAddQ); }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="confirm-q-btn">
              {editingQ ? 'Guardar alterações' : 'Adicionar pergunta'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
